import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { 
  TrendingUp, 
  Search, 
  Target, 
  Loader2, 
  Download, 
  FileText,
  BarChart3,
  Building2,
  ArrowLeft
} from "lucide-react";
import { AttachReportToListing } from '@/components/admin/AttachReportToListing';

const translations = {
  en: {
    title: "Market Study Generator",
    subtitle: "Generate comprehensive market analysis using AI",
    studyType: "Study Type",
    full: "Complete Study",
    fullDesc: "Full market analysis with all sections",
    competition: "Competition Analysis",
    competitionDesc: "In-depth competitor analysis",
    trends: "Market Trends",
    trendsDesc: "Industry trends and projections",
    companyProfile: "Company Profile",
    companyName: "Company Name",
    industry: "Industry",
    location: "Location",
    description: "Business Description",
    revenue: "Annual Revenue ($)",
    competitors: "Known Competitors (comma separated)",
    generate: "Generate Study",
    generating: "Generating...",
    downloadPdf: "Download PDF",
    preview: "Preview",
    result: "Generated Study",
    success: "Market study generated successfully",
    error: "Error generating study",
  },
  fr: {
    title: "Générateur d'Études de Marché",
    subtitle: "Générez des analyses de marché complètes avec l'IA",
    studyType: "Type d'Étude",
    full: "Étude Complète",
    fullDesc: "Analyse de marché complète avec toutes les sections",
    competition: "Analyse Concurrentielle",
    competitionDesc: "Analyse approfondie des concurrents",
    trends: "Tendances du Marché",
    trendsDesc: "Tendances et projections de l'industrie",
    companyProfile: "Profil de l'Entreprise",
    companyName: "Nom de l'Entreprise",
    industry: "Industrie",
    location: "Localisation",
    description: "Description de l'Activité",
    revenue: "Chiffre d'Affaires Annuel ($)",
    competitors: "Concurrents Connus (séparés par virgule)",
    generate: "Générer l'Étude",
    generating: "Génération...",
    downloadPdf: "Télécharger PDF",
    preview: "Aperçu",
    result: "Étude Générée",
    success: "Étude de marché générée avec succès",
    error: "Erreur lors de la génération",
  },
};

const industries = [
  "Technology", "Healthcare", "Manufacturing", "Retail", "Food & Beverage",
  "Construction", "Professional Services", "Transportation", "Finance", "Real Estate"
];

export default function MarketStudyGenerator() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [studyType, setStudyType] = useState<"full" | "competition" | "trends">("full");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    companyName: "",
    industry: "",
    location: "",
    description: "",
    revenue: "",
    competitors: "",
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dealIdParam = searchParams.get("deal");

  useEffect(() => {
    if (!dealIdParam) return;
    (async () => {
      let snap = await getDoc(doc(db, "dealSubmissions", dealIdParam));
      if (!snap.exists()) snap = await getDoc(doc(db, "deals", dealIdParam));
      if (snap.exists()) {
        const d = snap.data() as any;
        const g = d.rawFormData?.general || {};
        setFormData(prev => ({
          ...prev,
          companyName: g.legalName || g.tradeName || d.businessName || prev.companyName,
          industry: g.industry || d.industry || prev.industry,
          location: [g.locationCity, g.locationCountry].filter(Boolean).join(", ") || prev.location,
          description: g.description || d.description || prev.description,
          revenue: String(d.rawFormData?.financials?.revenueYear1 || d.revenueYear1 || prev.revenue || ""),
        }));
      }
    })();
  }, [dealIdParam]);

  const handleGenerate = async () => {
    if (!formData.companyName || !formData.industry) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { postApi } = await import('@/lib/api');
      const result = await postApi('/ai/generate-market-study', {
        dealId: dealIdParam || undefined,
        language,
        studyType,
        companyName: formData.companyName,
        industry: formData.industry,
        location: formData.location,
        description: formData.description,
        revenue: formData.revenue,
        competitors: formData.competitors,
      });
      if (!result?.success) throw new Error('Market study generation failed');
      setResult(result.marketStudy);

      toast({ title: t.success });
    } catch (error: any) {
      console.error("Market study generation error:", error);
      toast({ title: t.error, description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    // Simple PDF download using browser print
    const printWindow = window.open("", "_blank");
    if (printWindow && result) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Market Study - ${formData.companyName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
              h1 { color: #1D314D; border-bottom: 2px solid #CEAB65; padding-bottom: 10px; }
              h2 { color: #2E4663; margin-top: 30px; }
              p { margin: 10px 0; }
              .section { margin-bottom: 30px; }
            </style>
          </head>
          <body>
            <h1>Market Study: ${formData.companyName}</h1>
            <p><strong>Industry:</strong> ${formData.industry}</p>
            <p><strong>Location:</strong> ${formData.location}</p>
            <hr/>
            ${Object.entries(result).map(([key, value]) => `
              <div class="section">
                <h2>${key.replace(/([A-Z])/g, ' $1').trim()}</h2>
                <p>${value}</p>
              </div>
            `).join("")}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="space-y-6 p-6">
      {dealIdParam && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/staff/deals/${dealIdParam}`)}
          className="text-gray-300 hover:text-white hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === "fr" ? "Retour au dossier" : "Back to Deal"}
        </Button>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t.title}</h1>
          <p className="text-gray-400 mt-1">{t.subtitle}</p>
        </div>
        <BarChart3 className="h-8 w-8 text-amber-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-500" />
              {t.companyProfile}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Study Type Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">{t.studyType}</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={studyType === "full" ? "default" : "outline"}
                  onClick={() => setStudyType("full")}
                  className={studyType === "full" ? "bg-amber-500 hover:bg-amber-600" : "border-gray-600 text-gray-300"}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {t.full}
                </Button>
                <Button
                  variant={studyType === "competition" ? "default" : "outline"}
                  onClick={() => setStudyType("competition")}
                  className={studyType === "competition" ? "bg-amber-500 hover:bg-amber-600" : "border-gray-600 text-gray-300"}
                >
                  <Target className="h-4 w-4 mr-2" />
                  {t.competition}
                </Button>
                <Button
                  variant={studyType === "trends" ? "default" : "outline"}
                  onClick={() => setStudyType("trends")}
                  className={studyType === "trends" ? "bg-amber-500 hover:bg-amber-600" : "border-gray-600 text-gray-300"}
                >
                  <Search className="h-4 w-4 mr-2" />
                  {t.trends}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.companyName} *</Label>
              <Input
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.industry} *</Label>
              <Select
                value={formData.industry}
                onValueChange={(v) => setFormData({ ...formData, industry: v })}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.location}</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="e.g., Montreal, QC, Canada"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.description}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.revenue}</Label>
              <Input
                type="number"
                value={formData.revenue}
                onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">{t.competitors}</Label>
              <Input
                value={formData.competitors}
                onChange={(e) => setFormData({ ...formData, competitors: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Competitor A, Competitor B, Competitor C"
              />
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.generating}
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  {t.generate}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t.result}</CardTitle>
              {result && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  className="border-amber-500 text-amber-500 hover:bg-amber-500/20"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t.downloadPdf}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {Object.entries(result).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-700 pb-4 last:border-0">
                    <h3 className="text-amber-500 font-semibold mb-2 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{String(value)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                <BarChart3 className="h-16 w-16 mb-4 opacity-50" />
                <p>{t.preview}</p>
              </div>
            )}
            {result && (
              <AttachReportToListing
                reportType="market_study"
                reportContent={result}
                reportTitle={`Market Study: ${formData.companyName}`}
                language={language as 'en' | 'fr'}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}